// src/oura/merchant/model.ts
//
// What the merchant's Home and Work screens say, worked out with no React in
// it: the open asks in the order they matter, the plan grouped by status, the
// dates coming up, the term one line a month, and the named count Home shares
// with merchant Visibility.
//
// NOTHING HERE IS COMPUTED FROM TODAY'S DATE. Every "now" on these screens is
// a server fact: `schedule.currentMonth`, `lateDays`, the schedule's report
// dates. Dates are formatted, compared as ISO strings, or stepped from the
// server's own start date with the one anniversary rule (`addMonths`). The
// rule P2-4 set after `aiCheck.nextAt` slid forward a day every day holds on
// every merchant surface.
//
// The AI check is monthly and every monthly report carries it (YW, 28 Sep
// 2026), so its next date is the next report's. `aiCheck.nextAt` is not read
// for it: the server still computes it quarterly (BE handoff "Monthly AI
// check").

import {
    addMonths,
    type ClientPagePayload,
    type ClientPlanItem,
    type ClientPlanStatus,
    type ClientReportLink,
    type ClientTopQuery,
    type ClientUpcomingDate,
    type MerchantSchedule,
    type MerchantScheduleMonth,
} from "@service/aeo/clientReview";
import { dayDate, shortDateOrNull } from "../lib/oura";
import { MERCHANT_STATUS_ORDER, type Tone } from "../ui/tokens";
import type { StepState } from "../ui/Steps";
import { buildVisModel, type VisibilityInputs } from "../visibility/fromLive";
import { trendDelta, VIS_ENGINES, type VisEngine, type VisModel } from "../visibility/model";
import { asksOf, isOpen, type Ask, type TimingPart } from "./asks";
import { clientScans, questionsOnlyTrend, versionNumbers } from "../visibility/questionSets";
import type { AeoScoreHistoryEntry } from "@service/aeo";

/* ------------------------------------------------------------------------- *
 * Dates: compared and stepped from server dates, never from the browser clock.
 * Display goes through `dayDate` and `shortDateOrNull` (lib/oura), as on the
 * other merchant screens.
 * ------------------------------------------------------------------------- */

/**
 * A date-only value is a calendar day and is read at local midnight; a
 * timestamp is read as the instant it names. Appending a time to a timestamp
 * is the "Invalid Date" bug `shortDate` was fixed for, so the two are told
 * apart by length.
 */
export function parseDay(value: string | null | undefined): Date | null {
    if (!value) return null;
    const d = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
    return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number) => String(n).padStart(2, "0");
const isoOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** The local calendar day a date or timestamp falls on, as `YYYY-MM-DD`, so two can be compared as strings. */
export function isoDay(value: string | null | undefined): string | null {
    const d = parseDay(value);
    return d ? isoOf(d) : null;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/* ------------------------------------------------------------------------- *
 * The plan: this month's lines, by status.
 * ------------------------------------------------------------------------- */

/** Needs you first, Not started last; the server's order inside each status. */
export function sortPlanItems(items: ClientPlanItem[]): ClientPlanItem[] {
    const rank = (s: ClientPlanStatus) => MERCHANT_STATUS_ORDER.indexOf(s);
    return items.map((item, i) => ({ item, i })).sort((a, b) => rank(a.item.status) - rank(b.item.status) || a.i - b.i).map(({ item }) => item);
}

/** The phase for an over-line, with its stage: the Foundation build is stage 1, Growth stage 2. */
export function phaseOf(plan: ClientPagePayload["plan"]): { label: string; stage: string; tone: Tone } | null {
    if (!plan) return null;
    return plan.phase === "growth"
        ? { label: "Growth", stage: "Stage 2", tone: "green" }
        : { label: "Foundation", stage: "Stage 1", tone: "teal" };
}

/**
 * Catalogue lines the merchant is never shown (ourteam-backend#5109, #3946).
 *
 * `S2` "A Google profile per clinic" is the only one so far. Its connector was
 * dropped (ourteam-backend#3801), so nothing measures or reports it any more,
 * and a line we cannot report is a deliverable we would be promising.
 *
 * The backend still sends the row on purpose — it is the contract's own line,
 * and staff read the contract. So the filter is the FRONTEND's, and it belongs
 * at the one place the merchant's payload enters (`ClientPage`), never in each
 * screen: four screens each remembering to skip a code is four chances to
 * forget, and the count would disagree with the list the first time one did.
 */
const HIDDEN_PLAN_CODES = new Set(["S2"]);

/**
 * The merchant's payload: the same one staff read, minus the lines above.
 *
 * Returns the payload unchanged when there is nothing to drop, so the common
 * case allocates nothing and the object identity that memos depend on holds.
 */
export function merchantPayload(payload: ClientPagePayload): ClientPagePayload {
    // Report titles are the backend's (ourteam-backend#5900 numbers them "AI
    // Visibility Report V1, V2" and leaves a gated report's title bare), so
    // they are passed through as sent: a number worked out here from the
    // reports on hand would disagree with the backend's whenever a scan has
    // no report on the list.
    const items = payload.plan?.items;
    if (!items?.some((i) => HIDDEN_PLAN_CODES.has(i.code))) return payload;
    return { ...payload, plan: { ...payload.plan!, items: items.filter((i) => !HIDDEN_PLAN_CODES.has(i.code)) } };
}

export function planDone(items: ClientPlanItem[]): number {
    return items.filter((i) => i.status === "done").length;
}

/**
 * The pages the client bought, as the quotation counts them (YW, 30 Sep 2026).
 * Month 1's pages are B1's own number on its line; the contract's page units
 * are the monthly pages (G1). Any of them can be a new page or an existing one
 * improved, agreed with the client during the work. The Foundation build counts
 * its own pages; from month 2 the count is every page on the quotation.
 */
export function pagesOnQuotation(
    deliverables: { code: string; quantity: string | null; label?: string | null; note?: string | null; months?: number[]; cadence?: string | null }[] | undefined,
    monthlyPages: number,
    growth: boolean,
): number {
    const lines = deliverables ?? [];
    const lead = (q: string | null | undefined) => {
        const m = (q ?? "").match(/^\s*(\d+)/);
        return m ? Number(m[1]) : 0;
    };
    const code = (d: { code: string }) => d.code.trim().toUpperCase();
    const monthOnePages = lines.filter((d) => code(d) === "B1").reduce((n, d) => n + lead(d.quantity), 0);
    // A custom line sells pages when its words say "page" and its quantity starts with a number (H16).
    // Monthly: that many in each of its months; otherwise all in its first month.
    const custom = lines.filter((d) => /^X\d+$/.test(code(d)) && lead(d.quantity) > 0 && /\bpages?\b/i.test(`${d.label ?? ""} ${d.quantity ?? ""} ${d.note ?? ""}`));
    const monthly = (d: (typeof lines)[number]) => d.cadence === "monthly" || /\b(a|per) month\b/i.test(d.quantity ?? "");
    const customAll = custom.reduce((n, d) => n + lead(d.quantity) * (monthly(d) ? Math.max(1, d.months?.length ?? 1) : 1), 0);
    const customMonthOne = custom.filter((d) => (monthly(d) ? (d.months ?? []).includes(1) : Math.min(...(d.months?.length ? d.months : [99])) === 1)).reduce((n, d) => n + lead(d.quantity), 0);
    if (growth) return monthOnePages + monthlyPages + customAll;
    // Foundation counts month 1's pages; a quote with none there counts every page on it (H29).
    return monthOnePages + customMonthOne || monthlyPages + customAll;
}

/**
 * The lines a done count is taken over: every line but the `reported_by_hand`
 * ones (staging 3fdc24e6).
 *
 * `reported_by_hand` is its own state: a line with no automatic check, which
 * ourteam accounts for by hand. It is never "done", so counting it in the
 * denominator capped the count at a number it could never reach: THE LUSH
 * CLINIC has five of twelve, so its Foundation build could reach 58% and stop.
 * Dropping those lines from BOTH sides makes "N of M" a statement about the
 * work this screen can actually verify, and lets it reach M. The lines still
 * appear in the list, and each one's drawer says it is not part of the count.
 */
export function measuredItems(items: ClientPlanItem[]): ClientPlanItem[] {
    return items.filter((i) => i.status !== "reported_by_hand");
}

/**
 * The open ask a plan line waits on, or null. The server joins a line to its
 * asks (BE handoff "Plan line links and descriptions"). A line with none
 * joined, or whose asks are all answered, has nothing to answer whatever its
 * status says, so it opens as the work it is, never as every other open ask.
 */
export function openAskFor(item: ClientPlanItem, asks: Ask[]): string | null {
    return item.itemIds?.find((id) => asks.some((a) => a.item.id === id && isOpen(a.item))) ?? null;
}

/**
 * A plan line's name as a merchant reads it in a list or a title. A line from an earlier month that is
 * still open carries the same label as this month's ("AI visibility check" done for month 2 and not
 * started for month 1), so it says which month it is from (#4374). The server sends `month` only on such
 * lines; nothing is worked out from dates.
 */
export function planLineTitle(item: Pick<ClientPlanItem, "label" | "month">): string {
    return item.month ? `${item.label} · Month ${item.month}` : item.label;
}

/**
 * "What it is" for a plan line: the server's own description when it sends
 * one (BE handoff "Plan line links and descriptions"), else what was sold,
 * from the deliverable: its quantity and its note.
 */
export function lineDescription(item: ClientPlanItem, engagement: ClientPagePayload["engagement"]): string | null {
    if (item.description) return item.description;
    const d = engagement?.deliverables.find((x) => x.code === item.code);
    const parts = [d?.quantity, d?.note].filter((p): p is string => Boolean(p));
    return parts.length ? `${parts.join(". ")}.` : null;
}

/**
 * "When" for a plan line, from the months it was sold for: "Month 1",
 * "Every month from month 2", "Months 3, 6, 9 and 12". The cadence follows
 * when it says more than the months already do ("weekly, reported monthly").
 */
export function lineWhen(item: ClientPlanItem, engagement: ClientPagePayload["engagement"]): string | null {
    const d = engagement?.deliverables.find((x) => x.code === item.code);
    if (!d || d.months.length === 0) return null;
    const months = [...d.months].sort((a, b) => a - b);
    const last = engagement?.termMonths ?? months[months.length - 1];
    const runsToEnd = months.every((m, i) => m === months[0] + i) && months[months.length - 1] === last;
    const when = months.length === 1
        ? `Month ${months[0]}`
        : runsToEnd
            ? `Every month from month ${months[0]}`
            : `Months ${joinList(months.map(String))}`;
    const cadence = d.cadence && !["once", "monthly", "quarterly"].includes(d.cadence) ? ` ${capitalise(d.cadence)}.` : "";
    return `${when}.${cadence}`;
}

function joinList(parts: string[]): string {
    return parts.length <= 1 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/* ------------------------------------------------------------------------- *
 * Dates coming up.
 * ------------------------------------------------------------------------- */

export interface ComingDate {
    key: string;
    /** Date-only. */
    date: string;
    label: string;
    kind: ClientUpcomingDate["kind"];
    /** The date it was before it moved. */
    was: string | null;
    /** Why it moved, in the merchant's words. */
    reason: string | null;
    /** The ask it waits on, when it waits on one. */
    itemId: string | null;
    /** It moved to wait on an ask the merchant has since answered: its note is news, not a hold-up. */
    answered: boolean;
}

/** One line under each date in the dates drawer, by kind. Fixed copy, true of every plan. */
export const DATE_NOTE: Record<ComingDate["kind"], string | null> = {
    handover: "Read it, then say yes or ask for changes.",
    golive: "The pages go live on your site.",
    report: "Your numbers for the month, the AI check, and what we did.",
    ai_check: "We ask AI your questions again and see who it names.",
    call: "A call with your account manager.",
    other: null,
};

/**
 * The dates the merchant is waiting for, soonest first.
 *
 * The server's own list when it sends one (BE handoff "Upcoming dated
 * milestones for the merchant"); an empty list is its answer too. Without it,
 * the dates the payload already carries: this month's and next month's
 * report, unless it has gone out. A move from the last schedule publish
 * becomes a "Was" note on the date it landed on; which dates are still ahead
 * is the server's call, never the browser clock's.
 *
 * The AI check has no date of its own: it is monthly and comes with every
 * report, so the report row names both. A separate AI check date (the
 * server's quarterly `aiCheck.nextAt`, or an `ai_check` row) is left out.
 */
export function comingUp(payload: Pick<ClientPagePayload, "upcoming" | "schedule" | "packs" | "reports">): ComingDate[] {
    if (payload.upcoming) {
        const asks = asksOf(payload.packs);
        return payload.upcoming
            .filter((u) => u.kind !== "ai_check")
            .map((u, i) => ({ key: `u${i}-${u.date}`, ...u, ...waitsOn(u, asks) }));
    }
    const schedule = payload.schedule ?? null;
    const out: ComingDate[] = [];
    if (schedule) {
        const clockStart = parseDay(schedule.clockStart);
        for (const m of schedule.months) {
            if (m.termMonth !== schedule.currentMonth && m.termMonth !== schedule.currentMonth + 1) continue;
            if (reportSent(m.termMonth, payload.reports, clockStart)) continue;
            out.push({
                key: `report-${m.termMonth}`,
                date: m.reportDate,
                label: REPORT_ROW,
                kind: "report",
                was: movedFrom(schedule, m.reportDate),
                reason: null,
                itemId: null,
                answered: false,
            });
        }
    }
    return out.sort((a, b) => a.date.localeCompare(b.date));
}

/** A report row in Coming up and the dates drawer: every monthly report carries the AI check. */
export const REPORT_ROW = "Your monthly report and AI check";

/**
 * The reports still to come, soonest first: this month of the term onwards,
 * minus any month whose report has gone out (`reportSent`, the rule the dates
 * above use). Read off the published schedule, never worked out from today.
 */
export function upcomingReports(payload: Pick<ClientPagePayload, "schedule" | "reports">): MerchantScheduleMonth[] {
    const schedule = payload.schedule;
    if (!schedule) return [];
    const clockStart = parseDay(schedule.clockStart);
    return schedule.months
        .filter((m) => m.termMonth >= schedule.currentMonth && !reportSent(m.termMonth, payload.reports, clockStart))
        .sort((a, b) => a.reportDate.localeCompare(b.reportDate));
}

/**
 * The next AI check's date: the next monthly report's, from the schedule.
 * Null when no schedule is published or no report is left this term.
 */
export function nextAiCheckDate(payload: Pick<ClientPagePayload, "schedule" | "reports">): string | null {
    return upcomingReports(payload)[0]?.reportDate ?? null;
}

/**
 * What a server date still waits on. Its `reason` was written when it moved
 * ("Waiting on your prices.") and is not rewritten when the ask is answered,
 * so an answered ask turns it into "We have your answer." and takes the link
 * away. An ask the merchant was never sent has nothing to answer either: its
 * link would open every other ask instead.
 */
function waitsOn(date: ClientUpcomingDate, asks: Ask[]): Pick<ComingDate, "reason" | "itemId" | "answered"> {
    const ask = date.itemId ? asks.find((a) => a.item.id === date.itemId) : undefined;
    if (!ask) return { reason: date.reason, itemId: null, answered: false };
    if (isOpen(ask.item)) return { reason: date.reason, itemId: ask.item.id, answered: false };
    return { reason: date.was ? "We have your answer." : null, itemId: null, answered: true };
}

/**
 * One of the engagement's own documents (the quotation, the execution
 * timeline, the contract), not a report. The server's `kind` when it sends
 * one; on live it is absent today, so they are told by their names.
 */
export function isPlanDocument(r: Pick<ClientReportLink, "kind" | "title">): boolean {
    return r.kind === "document" || /\b(quotation|quote|proposal|timeline|contract|agreement)\b/i.test(r.title);
}

/**
 * A month's report, the thing Coming next counts down to. Not a filed
 * approved doc, not the Baseline Snapshot, not the quotation or the
 * timeline: those are sent in month one, and counting them marked month
 * one's report as gone out before it was (23 Oct 2026).
 */
export function isMonthlyReport(r: Pick<ClientReportLink, "kind" | "title">): boolean {
    if (r.kind === "doc_approved" || r.kind === "baseline_snapshot") return false;
    if (isPlanDocument(r)) return false;
    return !/\bbaseline\b/i.test(r.title);
}

/**
 * Whether month `termMonth`'s report has gone out. By the report's own
 * `termMonth` when the server sends one (BE handoff "Report tied to its term
 * month"), where null is a report tied to no month. Without it, by any report
 * dated inside the month's window, which runs from the anniversary of the
 * clock start. A report's date is when it was uploaded, often a timestamp, so
 * it is placed on the local day it names rather than cut to its first ten
 * characters.
 */
export function reportSent(termMonth: number, reports: ClientReportLink[], clockStart: Date | null): boolean {
    const from = clockStart ? isoOf(addMonths(clockStart, termMonth - 1)) : null;
    const until = clockStart ? isoOf(addMonths(clockStart, termMonth)) : null;
    return reports.some((r) => {
        // Only a month's own report moves the report date on.
        if (!isMonthlyReport(r)) return false;
        if (r.termMonth !== undefined) return r.termMonth === termMonth;
        const day = isoDay(r.date);
        return day !== null && from !== null && until !== null && day >= from && day < until;
    });
}

/** The date a published move started from, when one landed on `date`. */
function movedFrom(schedule: MerchantSchedule, date: string): string | null {
    const day = isoDay(date);
    return schedule.changed?.moves.find((m) => m.from && isoDay(m.to) === day)?.from ?? null;
}

/**
 * "Was Fri 9 Oct. Waiting on your prices." The slip note under a date, or
 * null when it never moved. Late (rose) while the move still stands to be
 * explained or answered; once the ask it waited on is answered it is plain.
 */
export function slipNote(d: ComingDate): TimingPart | null {
    const was = dayDate(d.was);
    if (!was) return null;
    return { text: d.reason ? `Was ${was}. ${d.reason}` : `Was ${was}.`, late: !d.answered };
}

/**
 * "Was Fri 9 Oct" in rose while a move still stands to be explained or
 * answered; null once it is settled, or when the date never moved. The
 * reason lives in the dates drawer.
 */
export function liveSlip(d: ComingDate): string | null {
    const was = dayDate(d.was);
    return was && !d.answered ? `Was ${was}` : null;
}

/** The first letter lowered, to run a label on after a comma: "Next: 28 Sep, your signature page comes to you". */
export function asClause(label: string): string {
    return label.charAt(0).toLowerCase() + label.slice(1);
}

/* ------------------------------------------------------------------------- *
 * The term, one line a month.
 * ------------------------------------------------------------------------- */

export interface MonthRow {
    /** `m<termMonth>`, or `later` for the compacted tail. */
    key: string;
    state: StepState;
    /** "Month 2 · Sep to Oct", or "Months 6 to 12 · Jan to Aug". */
    title: string;
    /** What lands, on one line. */
    lands: string;
    /** The month, or every month the compacted row stands for. */
    months: MerchantScheduleMonth[];
}

/** "18 Dec 2026 to 17 Jan 2027" to "Dec to Jan": the engine's window with the days and years taken out. */
export function monthsOnly(window: string): string {
    return window.replace(/\d+/g, "").replace(/\s+/g, " ").trim();
}

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface WindowParts {
    d1: number;
    m1: number;
    y1: number;
    d2: number;
    m2: number;
    y2: number;
}

/**
 * A month's window as the engine writes it (`fmt_window`, full or short month
 * names): "1 to 30 September 2026", "18 Sep to 17 Oct 2026" or "18 December
 * 2026 to 17 January 2027". Null for anything else.
 */
function parseWindow(window: string): WindowParts | null {
    const w = window.trim().replace(/\s+/g, " ");
    const mon = (s: string) => MONTH_SHORT.findIndex((m) => m.toLowerCase() === s.slice(0, 3).toLowerCase());
    let m = w.match(/^(\d{1,2}) to (\d{1,2}) ([A-Za-z]+) (\d{4})$/);
    if (m && mon(m[3]) >= 0) return { d1: +m[1], m1: mon(m[3]), y1: +m[4], d2: +m[2], m2: mon(m[3]), y2: +m[4] };
    m = w.match(/^(\d{1,2}) ([A-Za-z]+) to (\d{1,2}) ([A-Za-z]+) (\d{4})$/);
    if (m && mon(m[2]) >= 0 && mon(m[4]) >= 0) {
        // One year written: a window that runs from December into January started the year before.
        const y2 = +m[5];
        return { d1: +m[1], m1: mon(m[2]), y1: mon(m[2]) > mon(m[4]) ? y2 - 1 : y2, d2: +m[3], m2: mon(m[4]), y2 };
    }
    m = w.match(/^(\d{1,2}) ([A-Za-z]+) (\d{4}) to (\d{1,2}) ([A-Za-z]+) (\d{4})$/);
    if (m && mon(m[2]) >= 0 && mon(m[5]) >= 0) return { d1: +m[1], m1: mon(m[2]), y1: +m[3], d2: +m[4], m2: mon(m[5]), y2: +m[6] };
    return null;
}

/** "18 Sep to 17 Oct"; a year only where it is not the term's first: "18 Dec to 17 Jan 2027". */
function windowText(p: WindowParts, baseYear: number): string {
    const endYear = p.y2 !== baseYear ? ` ${p.y2}` : "";
    const startYear = p.y1 !== p.y2 && p.y1 !== baseYear ? ` ${p.y1}` : "";
    if (p.y1 === p.y2 && p.m1 === p.m2) return `${p.d1} to ${p.d2} ${MONTH_SHORT[p.m2]}${endYear}`;
    return `${p.d1} ${MONTH_SHORT[p.m1]}${startYear} to ${p.d2} ${MONTH_SHORT[p.m2]}${endYear}`;
}

/** Months past this many after the current one fold into one "later" row. */
const SHOWN_AHEAD = 3;

/**
 * Done, you are here, next, then later. Later months are greyed, and past
 * current + 3 they fold into one row, so a 12-month term reads in six lines.
 * States come from the server's `currentMonth`, never from today.
 */
export function monthRows(schedule: MerchantSchedule): MonthRow[] {
    const cur = schedule.currentMonth;
    // The exact dates of each month; a window the engine wrote in another shape keeps its month names.
    const baseYear = parseWindow(schedule.months[0]?.window ?? "")?.y1 ?? null;
    const dates = (window: string) => {
        const p = parseWindow(window);
        return p && baseYear ? windowText(p, baseYear) : monthsOnly(window);
    };
    const stateOf = (n: number): StepState => (n < cur ? "done" : n === cur ? "now" : n === cur + 1 ? "next" : "later");
    const shown = schedule.months.filter((m) => m.termMonth <= cur + SHOWN_AHEAD);
    const tail = schedule.months.filter((m) => m.termMonth > cur + SHOWN_AHEAD);
    // One month on its own is not worth folding.
    const rows = (tail.length > 1 ? shown : schedule.months).map((m): MonthRow => ({
        key: `m${m.termMonth}`,
        state: stateOf(m.termMonth),
        title: `Month ${m.termMonth} · ${dates(m.window)}`,
        lands: m.lands.join(" · "),
        months: [m],
    }));
    if (tail.length > 1) {
        const first = tail[0];
        const last = tail[tail.length - 1];
        const a = parseWindow(first.window);
        const b = parseWindow(last.window);
        const span =
            a && b && baseYear
                ? windowText({ d1: a.d1, m1: a.m1, y1: a.y1, d2: b.d2, m2: b.m2, y2: b.y2 }, baseYear)
                : `${monthsOnly(first.window).split(" to ")[0]} to ${monthsOnly(last.window).split(" to ").pop()}`;
        const lands = landsOf(tail);
        rows.push({
            key: "later",
            state: "later",
            title: `Months ${first.termMonth} to ${last.termMonth} · ${span}`,
            lands: lands.length > 3 ? `${lands.slice(0, 3).join(" · ")} and more` : lands.join(" · "),
            months: tail,
        });
    }
    return rows;
}

/** Every line that lands across these months, once each, in term order. */
export function landsOf(months: MerchantScheduleMonth[]): string[] {
    return months.flatMap((m) => m.lands).filter((line, i, all) => all.indexOf(line) === i);
}

/** "a report and AI check on the 16th of each month", when every report date shares a day. Null when they do not. */
export function reportCadence(schedule: MerchantSchedule): string | null {
    const days = schedule.months.map((m) => parseDay(m.reportDate)?.getDate());
    const day = days[0];
    if (day === undefined || days.some((d) => d !== day)) return null;
    return `a report and AI check on the ${ordinal(day)} of each month`;
}

function ordinal(n: number): string {
    const teen = n % 100 >= 11 && n % 100 <= 13;
    const suffix = teen ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th";
    return `${n}${suffix}`;
}

/**
 * The term's end date, rounded the way the backend rounds it.
 *
 * This used to be `d.setMonth(d.getMonth() + n)`, which OVERFLOWS: a term
 * starting 31 August "renewed" on 2 or 3 March instead of 28/29 February, so
 * the date the merchant reads disagreed with the schedule for exactly the
 * month-end contracts #3748 is about. It shares the one clamping `addMonths`
 * with `monthOf`.
 */
function termEnd(iso: string, n: number): string | null {
    const d = parseDay(iso);
    // DATE-ONLY out, built from local parts. `startedAt` is a date, and
    // `toISOString()` on a local midnight shifts it back across the UTC line,
    // and east of Greenwich the renewal date would render a day early.
    return d ? isoOf(addMonths(d, n)) : null;
}

/**
 * When the term renews. The PUBLISHED term end when there is one (#3715 /
 * ourteam-backend#4916), because that is the date the merchant was actually
 * told; the engine draws the term's dates with its own rules, so without the
 * published value these two can disagree by days on two of the merchant's own
 * screens. Otherwise #3748's clamped `termEnd`.
 */
export function renewsOn(payload: Pick<ClientPagePayload, "schedule" | "subscription">): string | null {
    if (payload.schedule?.termEnd) return payload.schedule.termEnd;
    const sub = payload.subscription;
    return sub?.startedAt ? termEnd(sub.startedAt, sub.termMonths) : null;
}

/**
 * "Annual plan · 18 Aug 2026 to 17 Aug 2027 · a report and AI check on the
 * 16th of each month": the term in one line, for the dates drawer's "Your
 * term". Null with neither a subscription nor a schedule.
 */
export function planSpanText(payload: Pick<ClientPagePayload, "schedule" | "subscription">): string | null {
    const { subscription, schedule } = payload;
    if (!subscription && !schedule) return null;
    const start = shortDateOrNull(schedule?.clockStart ?? subscription?.startedAt);
    const end = shortDateOrNull(renewsOn(payload));
    const span = start && end
        ? `${start} to ${end}`
        : start
            ? `From ${start}`
            // Nothing is dated until payment clears; the length is what was sold.
            : subscription && `Starts when your payment clears, for ${subscription.termMonths} months`;
    const parts = [subscription?.name, span, schedule && reportCadence(schedule)].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : null;
}

/* ------------------------------------------------------------------------- *
 * Sentences.
 * ------------------------------------------------------------------------- */

/**
 * Work's sentence, in parts the screen draws. With a dated go-live: "New
 * pages go live Wed 14 Oct", then ", moved from Fri 9 Oct" (rose while the
 * move is live), then the reason. Otherwise how many pieces of work wait on
 * the merchant. No plan: where the work will appear.
 */
export type WorkSentence =
    | { kind: "golive"; lead: string; moved: { text: string; late: boolean } | null; reason: string | null }
    | { kind: "text"; text: string };

export function workSentence(items: ClientPlanItem[], dates: ComingDate[]): WorkSentence {
    const golive = dates.find((d) => d.kind === "golive");
    const when = dayDate(golive?.date);
    if (golive && when) {
        const was = dayDate(golive.was);
        return {
            kind: "golive",
            lead: `${golive.label} ${when}`,
            moved: was ? { text: `moved from ${was}`, late: !golive.answered } : null,
            reason: golive.reason?.trim() || null,
        };
    }
    if (items.length === 0) return { kind: "text", text: "Your work appears here once your plan starts." };
    const waiting = items.filter((i) => i.status === "waiting_on_you").length;
    return { kind: "text", text: waiting > 0 ? `${plural(waiting, "piece of work waits", "pieces of work wait")} on you.` : "Nothing waits on you." };
}

/* ------------------------------------------------------------------------- *
 * How you're found: the named count merchant Visibility shows.
 * ------------------------------------------------------------------------- */

export interface FoundReading {
    named: number;
    of: number;
    /** When the scan behind the count was read. */
    checkedAt: string | null;
    /**
     * The count on the scan before, when the two can be compared, or null.
     * Only where both asked the same questions (rule 2): on live that needs
     * the per-scan named history (BE handoff "Named-over-time history"), so
     * there is no change to show until it ships.
     */
    previous: { named: number; at: string } | null;
}

/**
 * Home's "How AI names you" card: the high-level reading of the same scan
 * the Visibility page shows, so each figure here matches the one there. The
 * per-engine counts, the full rival list and own-site citations stay on
 * Visibility; Home names one of each.
 */
export interface StandingReading {
    /** The named count per scan, oldest first, on today's way of measuring only. */
    /** `scan`: the scan's number (V2), as the scan picker and reports name it; null when unknown. */
    trend: { at: string | null; named: number; scan: number | null }[];
    /**
     * When today's way of measuring started, if an earlier scan was measured
     * another way: why Home's line can be shorter than Visibility's chart.
     */
    measuredSince: string | null;
    /**
     * The engine that names them least: the one Visibility's sentence names.
     * Null with fewer than two engines measured, or on a tie (weakestEngine).
     */
    weakest: { key: VisEngine; label: string } | null;
    /** The rival named in their place on the most questions, or null when none is. */
    topRival: string | null;
}

/** Search Console's average position, as the Visibility page's Google tile reads it. */
export interface GoogleReading {
    avgPosition: number;
}

/** What Home reads beside the client page: the business's visibility payload, boiled down. */
export interface MerchantLive {
    /** Null when there is no scored scan to count from. */
    found: FoundReading | null;
    /** Searches that brought visits, most clicks first. Null when Search Console has none to give. */
    searches: ClientTopQuery[] | null;
    /** Null when there is no scored scan to read from. */
    standing: StandingReading | null;
    /** Null when Search Console is not connected or has no position yet. */
    google: GoogleReading | null;
    /**
     * Each AI engine's own count, in Visibility's order (16 Oct 2026). An
     * engine the scan did not measure has `named: null`: "not measured",
     * never a zero. Null when there is no scored scan.
     */
    engines: { key: VisEngine; label: string; named: number | null; of: number | null }[] | null;
    /**
     * Who AI names instead: the 3 rivals that take the most questions from
     * the business, as Visibility's Competitors tab counts them. Empty when no
     * rival has a count; null when there is no scored scan.
     */
    rivals: { name: string; on: number; takes: number }[] | null;
}

function standingOf(model: VisModel, history: AeoScoreHistoryEntry[]): StandingReading {
    // By question set, as merchant Visibility draws it (#4657): the line breaks only where the questions changed.
    const t = questionsOnlyTrend(model.trend, history);
    const numbers = versionNumbers(clientScans(history));
    const scanOn = new Map(history.map((h) => [h.captured_at, numbers.get(h.assessment_id) ?? null]));
    // The line starts where today's way of measuring starts (trendBreak.ts),
    // so an earlier scan on other questions never reads as a rise or a fall.
    // A history of mentions rather than questions is no count to draw.
    const trend = t.unit !== "questions"
        ? []
        : t.points.slice(t.breakIndex ?? 0).flatMap((p) => (p.values.all === null ? [] : [{ at: p.date, named: p.values.all, scan: (p.date && scanOn.get(p.date)) || null }]));
    const weakest = model.headline.weakest;
    // Most questions first; a stable sort keeps Visibility's order on a tie.
    const top = model.rivals.filter((r) => r.scored && r.takes > 0).sort((a, b) => b.takes - a.takes)[0];
    return {
        trend,
        measuredSince: t.unit === "questions" && (t.breakIndex ?? 0) > 0 ? t.points[t.breakIndex ?? 0]?.date ?? null : null,
        weakest: weakest ? { key: weakest.key, label: weakest.label } : null,
        topRival: top?.name ?? null,
    };
}

/** How many searches the visits drawer lists. */
export const TOP_SEARCHES = 5;

/**
 * The reading from the same inputs merchant Visibility builds its page from
 * (visibility/fromLive.ts): its headline count, and its change through the
 * same `trendDelta`, which holds the question-set break rule. One model, so
 * the tile and the page cannot disagree. Null inputs, or inputs with no
 * visibility block, are no scan to count from, as Visibility's empty state
 * says.
 *
 * The searches are Search Console's keywords sorted by clicks. That list is
 * the top 50 by impressions, so a click-heavy search outside it can be missed
 * until the client page sends its own (BE handoff "Top searches by clicks on
 * the client page"), which Home prefers when present.
 */
export function merchantReading(inputs: VisibilityInputs | null): MerchantLive {
    const sp = inputs?.searchPerformance;
    const searches = sp?.connected && sp.keywords
        ? sp.keywords
            .filter((k) => k.clicks > 0)
            .sort((a, b) => b.clicks - a.clicks)
            .slice(0, TOP_SEARCHES)
            .map((k) => ({ query: k.query, clicks: k.clicks, branded: k.branded }))
        : null;
    if (!inputs?.vis) return { found: null, searches, standing: null, google: null, engines: null, rivals: null };
    const model = buildVisModel(inputs, null);
    const g = model.google;
    const google = g.connected && g.avgPosition !== null ? { avgPosition: g.avgPosition } : null;
    const { named, of } = model.headline;
    if (of <= 0) return { found: null, searches, standing: null, google, engines: null, rivals: null };
    const change = trendDelta(model.trend, "all");
    return {
        found: {
            named,
            of,
            checkedAt: model.capturedAt || null,
            previous: change?.comparable && change.since ? { named: named - change.value, at: change.since } : null,
        },
        searches,
        standing: standingOf(model, inputs.history ?? []),
        google,
        engines: VIS_ENGINES.map((key) => {
            const e = model.engines[key];
            return { key, label: e.label, named: e.named, of: e.of };
        }),
        // Most questions taken first; a stable sort keeps Visibility's order on a tie.
        rivals: model.rivals
            .filter((r) => r.scored && r.takes > 0)
            .sort((a, b) => b.takes - a.takes)
            .slice(0, 3)
            .map((r) => ({ name: r.name, on: r.on, takes: r.takes })),
    };
}
